import React, {lazy, Suspense, useEffect, useRef, useState} from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate, useParams} from 'react-router-dom';
import {PrivateRoute} from "./components/layout/PrivateRoute";
import CookieNotice from "./components/layout/CookieNotice";
import Footer from "./components/layout/Footer";
import ScrollToTop from "./components/layout/scrollToTop";
import Header from "./components/layout/Header";
import MetaPixel from "./pages/MetaPixel";
import CheckoutFooter from "./components/layout/CheckoutFooter";

const HomePage = lazy(() => import('./pages/HomePage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const PromotionsInfoPage = lazy(() => import('./pages/PromotionsInfoPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const FacebookCheckoutPage = lazy(() => import('./pages/FacebookCheckoutPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const AddressesPage = lazy(() => import('./pages/AddressesPage'));
const StoredPaymentsPage = lazy(() => import('./pages/StoredPaymentsPage'));
const OrderPage = lazy(() => import('./pages/OrderPage'));
const ShippingPage = lazy(() => import('./pages/Shipping'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const ReturnPolicyPage = lazy(() => import('./pages/ReturnPolicyPage'));
const ShippingPolicyPage = lazy(() => import('./pages/ShippingPolicy'));
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const ProductSubCategoryPage = lazy(() => import('./pages/ProductSubCategoryPage'));
const ProductCategoryPage = lazy(() => import('./pages/ProductCategoryPage'));
const PhotoLicensePage = lazy(() => import('./pages/PhotoLicensePage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const GiftBlogPost = lazy(() => import('./pages/BlogWhatToGetThePersonWhoHasEverything'));
const PracticalGiftSuggestions = lazy(() => import('./pages/BlogPracticalGiftSuggestions'));
const IntentionalWaysToSave = lazy(() => import('./pages/BlogFullfillingWaysToSaveMoney'));
const StickingWithExercise = lazy(() => import('./pages/BlogStickingWithExercise'));

function ProductDetailRedirect() {
    const {id} = useParams();
    return <Navigate to={`/product/${id}`} replace/>;
}

function App() {
    const [cookieAccepted, setCookieAccepted] = useState(() => {
        return localStorage.getItem('cookieConsent') === 'true';
    });

    const cookieRef = useRef(null);
    const [cookieHeight, setCookieHeight] = useState(0);

    const handleAccept = () => {
        localStorage.setItem('cookieConsent', 'true');
        setCookieAccepted(true);
    };

    useEffect(() => {
        function updateHeight() {
            if (cookieRef.current) {
                setCookieHeight(cookieRef.current.offsetHeight);
            }
        }

        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [cookieAccepted]);

    useEffect(() => {
        if (cookieAccepted) return;

        const ref = document.referrer || "";
        const fbDomains = [
            "facebook.com",
            "l.facebook.com",
            "lm.facebook.com",
            "m.facebook.com",
            "instagram.com",
        ];

        const fromFacebook = fbDomains.some(domain => ref.includes(domain));

        if (fromFacebook) {
            handleAccept();
        }
    }, [cookieAccepted]);

    return (
        <Router>
            <MetaPixel/>
            <ScrollToTop/>
            <div className={`flex flex-col min-h-screen ${!cookieAccepted ? 'pb-16' : ''}`}>
                {/*<TopNoticeBar/>*/}
                {/*<TopNoticeBar*/}
                {/*    message={'🚚 We currently ship only to the continental United States (excludes Alaska + Hawaii)'}*/}
                {/*    autoHideDelay={10000}/>*/}
                <Header/>

                <main className={`flex-grow ${!cookieAccepted ? 'pb-16' : ''}`}>
                    <Suspense fallback={<div>Loading...</div>}>
                        <Routes>
                            <Route path="/" element={<HomePage/>}/>
                            <Route path="/productdetailpage/:id" element={<ProductDetailRedirect/>}/>
                            <Route path="/product/:id" element={<ProductDetailPage/>}/>
                            <Route path="/products/:category/" element={<ProductCategoryPage/>}/>
                            <Route path="/products/:category/:subcategory?" element={<ProductSubCategoryPage/>}/>
                            <Route path="/about-us" element={<AboutUsPage/>}/>
                            <Route path="/promotions-info" element={<PromotionsInfoPage/>}/>
                            <Route path="/register" element={<RegisterPage/>}/>
                            <Route path="/cart" element={<CartPage/>}/>
                            <Route path="/meta-checkout" element={<FacebookCheckoutPage/>}/>
                            <Route path="/login" element={<LoginPage/>}/>
                            <Route path="/forgotpassword" element={<ForgotPasswordPage/>}/>
                            <Route path="/contactus" element={<ContactUsPage/>}/>
                            <Route path="/shipping" element={<ShippingPage/>}/>
                            <Route path="/billing" element={<BillingPage/>}/>
                            <Route path="/orderconfirmation" element={<OrderConfirmation/>}/>
                            <Route path="/privacy-policy" element={<PrivacyPolicyPage/>}/>
                            <Route path="/faq" element={<FaqPage/>}/>
                            <Route path="/terms-of-use" element={<TermsOfUsePage/>}/>
                            <Route path="/return-policy" element={<ReturnPolicyPage/>}/>
                            <Route path="/shipping-policy" element={<ShippingPolicyPage/>}/>
                            <Route path="/photolicensing" element={<PhotoLicensePage/>}/>
                            <Route path="/verifyemail" element={<VerifyEmailPage/>}/>
                            <Route path="/resetpassword" element={<ResetPasswordPage/>}/>
                            <Route path="/search" element={<SearchPage/>}/>
                            <Route path="/blog/what-to-get-the-person-who-has-everything" element={<GiftBlogPost/>}/>
                            <Route path="/blog/practical-gifts" element={<PracticalGiftSuggestions/>}/>
                            <Route path="/blog/intentional-money-saving-tips" element={<IntentionalWaysToSave/>}/>
                            <Route path="/blog/sticking-with-exercise" element={<StickingWithExercise/>}/>
                            <Route element={<PrivateRoute/>}>
                                <Route path="/accountaddresses" element={<AddressesPage/>}/>
                                <Route path="/account" element={<AccountPage/>}/>
                                <Route path="/storedpayments" element={<StoredPaymentsPage/>}/>
                                <Route path="/orders" element={<OrderPage/>}/>
                            </Route>
                        </Routes>
                    </Suspense>
                </main>
                <CheckoutFooter cookieAccepted={cookieAccepted} cookieHeight={cookieHeight}/>
                <Footer className="bg-primary text-text-white relative z-30"/>
                {!cookieAccepted && <CookieNotice ref={cookieRef} onAccept={handleAccept}/>}
            </div>
        </Router>
    );
}

export default App;
